import './ConversationList.scss'

import { Input, List, Spinner, useDisclosure } from '@chakra-ui/react'
import { mdiCheck, mdiClose, mdiDeleteOutline, mdiMessageOutline, mdiRenameOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { Link } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import difference from 'lodash/difference'
import union from 'lodash/union'
import { ChangeEvent, Dispatch, FC, memo, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '@/analytics'
import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion'
import { Box } from '@/components/ui/box'
import { Flex } from '@/components/ui/flex'
import { Skeleton } from '@/components/ui/skeleton'
import { Stack } from '@/components/ui/stack'
import { Tooltip } from '@/components/ui/tooltip'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { pinnedChatsAtom, setPinnedChats } from '@/store/gigiChat/PinAtom.ts'
import { IConversationListItem } from '@/types/gigiChat/Conversation'
import { IConversationListProps } from '@/types/gigiChat/ConversationListProps'
import { IConversationSettings, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'
import { deepClone } from '@/utils'

import AgentHeadset from './components/agentHeadset'
import CopyConversation from './components/copyConversation/CopyConversation.tsx'
import DeleteConversationDrawer from './components/deleteConversationDrawer'
import PinIcon from './components/pin/PinIcon.tsx'
import TranslateTitle from './components/translateTitle'

interface IConversationListItemProps {
    item: IConversationListItem
    isActive: '' | 'active'
    editKey: string
    index: number
    editIndex: string | null
    value: string
    inProgress: boolean
    actions: {
        copy: boolean
        delete: boolean
        edit: boolean
        pin: boolean
    }

    isPinned: boolean
    processingStrategy: TProcessingStrategy | undefined
    onPin: (chatId: string) => void
    onOpen: () => void
    onEditClose: () => void
    onEditOpen: (editIndex: string, value?: string) => void
    onClick: IConversationListProps['onClick']
    onTitleChange: (chatId: string, settings: IConversationSettings) => void
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void
    setIdToDelete: Dispatch<SetStateAction<string | null>>
}

const ConversationListItem: FC<IConversationListItemProps> = ({
    item,
    isActive,
    inProgress,
    editKey,
    index,
    editIndex,
    value,
    actions,
    processingStrategy,
    onTitleChange,
    isPinned,
    onPin,
    onOpen,
    onClick,
    onEditClose,
    onEditOpen,
    handleChange,
    setIdToDelete,
}) => {
    const { t } = useTranslation()
    const isEditing = editIndex === editKey
    const isNewChat = item.settings.title === 'New Chat'
    const isTranslation = processingStrategy === 'Translation'
    const isAgentConversation = item.settings.processingStrategy === 'QA' && item.settings.processingStrategyQa.mode === 'agent'

    const getBaseUrl = () => {
        switch (processingStrategy) {
            case 'DocAnalysis':
                return 'documents'
            case 'Translation':
                return `translate`
            default:
                return `chat`
        }
    }

    return (
        <List.Item
            padding={isTranslation ? '16px' : '8px'}
            borderRadius="2px"
            className={`conversationItem ${item.chatId} ${isActive} chat-row ${isPinned ? 'pinned' : ''}`}
            position="relative"
            minH="36px"
            borderBottom={!isNewChat && isTranslation ? '1px solid' : ''}
            borderBottomColor="var(--chakra-colors-gray-300)"
        >
            <Flex alignItems="top">
                {isEditing ? (
                    <Box
                        as="form"
                        w="100%"
                        onSubmit={(e) => {
                            e.preventDefault()
                            if (value) {
                                onTitleChange(item.chatId, item.settings)
                            }
                        }}
                    >
                        <Flex w="100%">
                            <Box width="20px" mr="10px">
                                {isAgentConversation ? (
                                    <AgentHeadset chatId={item.chatId} />
                                ) : (
                                    <Icon path={mdiMessageOutline} size="20px" color="var(--chakra-colors-gray-600)" />
                                )}
                            </Box>
                            <Input
                                value={value}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        onEditClose()
                                    }
                                }}
                                variant="flushed"
                                fontSize="14px"
                                letterSpacing="0.25px"
                                type="string"
                                height="25px"
                                mt="-5px"
                                disabled={inProgress}
                                autoFocus={true}
                                colorPalette={'orange'}
                            />
                            <Tooltip content="Cancel" variant="gsk">
                                <Box cursor="pointer" ml="22px" w="40px" onClick={onEditClose}>
                                    <Icon path={mdiClose} size="20px" />
                                </Box>
                            </Tooltip>
                            {!inProgress && (
                                <Tooltip content="Save" variant="gsk">
                                    <Box
                                        cursor={value ? 'pointer' : 'default'}
                                        ml="16px"
                                        w="40px"
                                        onClick={value ? () => onTitleChange(item.chatId, item.settings) : undefined}
                                    >
                                        <Icon path={mdiCheck} size="20px" />
                                    </Box>
                                </Tooltip>
                            )}
                            {inProgress && (
                                <Box h="14px" ml="16px" w="40px">
                                    <Spinner w="14px" h="14px" />
                                </Box>
                            )}
                        </Flex>
                    </Box>
                ) : (
                    <>
                        <Flex
                            alignItems="center"
                            flexGrow={1}
                            flexShrink={1}
                            minWidth="0"
                            cursor={editIndex ? 'default' : 'pointer'}
                            onClick={
                                editIndex
                                    ? () => {}
                                    : () => {
                                          onClick(item.settings)
                                          track('chat-select')
                                      }
                            }
                            {...(editIndex
                                ? {
                                      _after: {
                                          content: '""',
                                          position: 'absolute',
                                          width: '100%',
                                          height: '100%',
                                          bottom: '0',
                                          left: '0',
                                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                          _dark: {
                                              backgroundColor: 'rgba(28, 29, 30, 0.7)',
                                          },
                                      },
                                  }
                                : {})}
                        >
                            {actions.pin && (
                                <PinIcon isPinned={isPinned} onPin={onPin} chatId={item.chatId} isAgentConversation={isAgentConversation} />
                            )}
                            <Box minWidth={0}>
                                <Link to={`/${getBaseUrl()}/${item.chatId}`}>
                                    {!isTranslation && (
                                        <Text
                                            truncate
                                            fontSize="14px"
                                            ml="10px"
                                            mr="8px"
                                            width="auto"
                                            letterSpacing="0.25px"
                                            wordBreak="break-all"
                                        >
                                            {item.settings?.title ?? t('chat.newChat.conversationName')}
                                        </Text>
                                    )}

                                    {isTranslation && <TranslateTitle item={item} />}
                                </Link>
                            </Box>
                        </Flex>
                        <Flex gap="10px" hidden={(item.settings.title === 'New Chat' && index === 0) || !!editIndex || false}>
                            {actions.edit && (
                                <Tooltip content="Rename Conversation" variant="gsk" positioning={{ placement: 'left' }}>
                                    <Box
                                        onClick={() => {
                                            onEditOpen(editKey, item.settings?.title)
                                        }}
                                        cursor="pointer"
                                        ml="auto"
                                        className="actionConversation"
                                    >
                                        <Icon path={mdiRenameOutline} size="20px" />
                                    </Box>
                                </Tooltip>
                            )}
                            {actions.copy && <CopyConversation chatId={item.chatId} />}
                            {actions.delete && (
                                <Tooltip content="Delete Conversation" variant="gsk" positioning={{ placement: 'left' }}>
                                    <Box
                                        onClick={() => {
                                            onOpen()
                                            setIdToDelete(item.chatId)
                                        }}
                                        cursor="pointer"
                                        className="actionConversation"
                                    >
                                        <Icon path={mdiDeleteOutline} size="20px" />
                                    </Box>
                                </Tooltip>
                            )}
                        </Flex>
                    </>
                )}
            </Flex>
        </List.Item>
    )
}

// split declaration of component and memo so that it gets named in dev tools
const ConversationListItemMemo: FC<IConversationListItemProps> = memo(ConversationListItem, (prevProps, nextProps) => {
    // when editing we only want to:
    // * re-render the edit list item when doing normal typing
    // * re-render the whole list when editing is starting or stopping (to support the fade out of non-edit items)
    if (prevProps.editIndex || nextProps.editIndex) {
        // previous conversation item was editing
        if (prevProps.editIndex === prevProps.editKey) {
            return false
        }

        // next conversation item is editing
        if (nextProps.editIndex === nextProps.editKey) {
            return false
        }

        // figure out how to re-render only on begin edit mode and end edit mode.
        if (prevProps.editIndex !== nextProps.editIndex) {
            // editing is either starting or ending
            return false
        }
        // otherwise just assume not to re-render
        return true
    } else {
        return false
    }
})

const ConversationList = ({ activeChatId, conversations, onDelete, onClick, saveTitle, processingStrategy }: IConversationListProps) => {
    const { open, onOpen, onClose } = useDisclosure()
    const [idToDelete, setIdToDelete] = useState<string | null>(null)
    const [editIndex, setEditIndex] = useState<string | null>(null)
    const [value, setValue] = useState<string>('')
    const [inProgress, setInProgress] = useState<boolean>(false)
    const labelColor = useColorModeValue('var(--chakra-colors-status-info)', 'var(--chakra-colors-gray-500)')
    const [pinnedChats] = useAtom(pinnedChatsAtom)
    const isTranslation = processingStrategy === 'Translation'

    const conversationKeys = useMemo(() => Object.keys(conversations), [conversations])

    const initialVisibleConversations = useMemo(() => conversationKeys.slice(), [conversationKeys])

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setValue(event.target.value)
        },
        [setValue],
    )

    const onTitleChange = useCallback(
        (chatId: string, settings: IConversationSettings) => {
            const newSettings = deepClone<object>(settings) as IConversationSettings
            newSettings.title = value
            setInProgress(true)
            void saveTitle(chatId, newSettings).then((results) => {
                if (results && results.ok) {
                    setValue('')
                    setEditIndex(null)
                }
                setInProgress(false)
            })
        },
        [setInProgress, setEditIndex, setValue, saveTitle, value],
    )

    const onEditClose = useCallback(() => {
        setEditIndex(null)
        setValue('')
    }, [setEditIndex, setValue])

    const onEditOpen = useCallback(
        (editIndex: string, value?: string) => {
            setEditIndex(editIndex)
            if (value) {
                setValue(value)
            }
        },
        [setEditIndex, setValue],
    )

    const onPin = useCallback(
        (chatId: string) => {
            if (pinnedChats.includes(chatId)) {
                setPinnedChats(difference(pinnedChats, [chatId]))
                track('unpin-conversation')
            } else {
                setPinnedChats(union(pinnedChats, [chatId]))
                track('pin-conversation')
            }
        },
        [pinnedChats],
    )

    const getLabelData = (label: string) => {
        return conversations[label].map((item: IConversationListItem, index: number) => {
            const isActive = (activeChatId === '' && !index) || activeChatId === item.chatId ? 'active' : ''
            const editKey = item.chatId
            const actions = {
                copy: processingStrategy !== 'Translation',
                delete: true,
                edit: processingStrategy !== 'Translation',
                pin: processingStrategy !== 'Translation',
            }

            return (
                <ConversationListItemMemo
                    key={item.chatId + index}
                    item={item}
                    isActive={isActive}
                    inProgress={inProgress}
                    editKey={editKey}
                    editIndex={editIndex}
                    index={index}
                    value={value}
                    onTitleChange={onTitleChange}
                    onPin={onPin}
                    isPinned={pinnedChats.includes(item.chatId)}
                    onOpen={onOpen}
                    onClick={onClick}
                    onEditClose={onEditClose}
                    onEditOpen={onEditOpen}
                    handleChange={handleChange}
                    setIdToDelete={setIdToDelete}
                    actions={actions}
                    processingStrategy={processingStrategy}
                />
            )
        })
    }

    const generateConversationList = () => {
        return (
            <AccordionRoot variant="conversationList" defaultValue={initialVisibleConversations} multiple>
                {conversationKeys.map((label: string) => {
                    return (
                        <AccordionItem border="none" key={label} value={label}>
                            <AccordionItemTrigger justifyContent="space-between">
                                <Text color={labelColor} opacity="0.7" fontSize="12px">
                                    {label}
                                </Text>
                            </AccordionItemTrigger>
                            <AccordionItemContent px={isTranslation ? '0' : 'var(--chakra-space-4)'}>
                                <Box className="accordion-item-panel">{getLabelData(label)}</Box>
                            </AccordionItemContent>
                        </AccordionItem>
                    )
                })}
            </AccordionRoot>
        )
    }

    if (conversationKeys.length === 0) {
        return (
            <Stack>
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
                <Skeleton height="25px" />
            </Stack>
        )
    }
    return (
        <List.Root variant="gigi">
            {generateConversationList()}
            {open && (
                <DeleteConversationDrawer
                    isOpen={open}
                    onClose={() => {
                        onClose()
                        setIdToDelete(null)
                    }}
                    onDelete={() => {
                        if (idToDelete) {
                            onDelete(idToDelete)
                        }
                    }}
                />
            )}
        </List.Root>
    )
}

export default ConversationList
